import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  AutoComplete,
  Select,
  Button,
  Row,
  Col,
  Space,
  notification,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";

import {
  clearTodayBigPrizes,
  getSlabsList,
} from "../../../../controllers/V1/smartRtp/smartRtpController";

const { Option } = Select;

function newConfigId() {
  return `bps_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Keep only valid 4-digit POS numbers. */
function parsePosNumbersFromText(text) {
  return String(text || "")
    .split(/[\s,;|\n\r]+/)
    .map((m) => m.replace(/\D/g, ""))
    .filter((m) => /^\d{4}$/.test(m));
}

function normalizePosTag(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return /^\d{4}$/.test(digits) ? digits : null;
}

function downloadSampleCsv() {
  const csv = "pos_number\n1014\n1025\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "smart_rtp_big_prize_pos_sample.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function normalizeIntField(value) {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function intFieldProps() {
  return {
    getValueFromEvent: (e) => normalizeIntField(e?.target?.value ?? e),
    getValueProps: (value) => ({
      value: value == null || value === "" ? undefined : String(value),
    }),
    normalize: normalizeIntField,
  };
}

function buildSlabAmountOptions(slabs) {
  const rows = (slabs || [])
    .filter((s) => s && s.status !== "D" && s.status !== "I")
    .map((s) => {
      const amount = Number(s.fixed_amount);
      if (!Number.isFinite(amount) || amount < 0) return null;
      return {
        amount,
        label: `${s.prize_category || "Slab"} · ${amount}${
          s.is_big_prize_candidate ? " · Big" : ""
        } · ${s.slab_name || s.slab_code || `slot ${s.slot_index || ""}`}`.trim(),
        sortBoost: s.is_big_prize_candidate || s.prize_category === "High" ? 0 : 1,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.sortBoost - b.sortBoost || b.amount - a.amount);

  // Unique by amount (keep first / preferred label)
  const seen = new Set();
  const options = [];
  for (const row of rows) {
    const key = String(row.amount);
    if (seen.has(key)) continue;
    seen.add(key);
    options.push({
      value: String(row.amount),
      label: row.label,
    });
  }
  return options;
}

/**
 * Big Prize & Time Slot Configuration (Settings form section).
 * Forced wins for listed POS numbers — excluded from RTP.
 */
export default function BigPrizeTimeSlotsField({ field, gameId = null }) {
  const form = Form.useFormInstance();
  const fileRefs = useRef({});
  const [slabOptions, setSlabOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const loadSlabs = async () => {
      try {
        const condition = { status: "A" };
        if (gameId) {
          condition.$or = [
            { game_id: gameId },
            { slab_scope: "Global" },
            { game_id: null },
          ];
        }
        const res = await getSlabsList({
          type: "",
          condition,
          limit: 0,
          skip: 0,
          sort: { prize_category: 1, slot_index: 1 },
        });
        if (cancelled) return;
        if (res?.status === true) {
          setSlabOptions(buildSlabAmountOptions(res.result || []));
        } else {
          setSlabOptions([]);
        }
      } catch (error) {
        if (!cancelled) setSlabOptions([]);
      }
    };
    loadSlabs();
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const autocompleteOptions = useMemo(() => slabOptions, [slabOptions]);

  const importCsvIntoConfig = (listIndex, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const posNumbers = parsePosNumbersFromText(String(reader.result || ""));
      const slots = form.getFieldValue(field.name) || [];
      const current = slots[listIndex]?.pos_numbers || [];
      const merged = Array.from(new Set([...current.map(String), ...posNumbers]));
      const next = [...slots];
      next[listIndex] = { ...next[listIndex], pos_numbers: merged };
      form.setFieldsValue({ [field.name]: next });
      notification.success({
        message: "CSV imported",
        description: `${posNumbers.length} POS number(s) loaded (4-digit only).`,
      });
    };
    reader.readAsText(file);
  };

  return (
    <Form.List name={field.name}>
      {(fields, { add, remove }) => (
        <div style={{ width: "100%", marginTop: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <h6 style={{ margin: 0, color: "#6f42c1" }}>
              # {field.label || "Attack Mode & Time Slot Configuration"}
            </h6>
            <Space wrap>
              <Link
                to={
                  gameId
                    ? `/smart-rtp/big-prize-monitor?gameId=${gameId}`
                    : "/smart-rtp/big-prize-monitor"
                }
                className="btn btn-outline-primary btn-sm"
              >
                Open POS Monitor
              </Link>
              <Button
                danger
                onClick={async () => {
                  const res = await clearTodayBigPrizes({
                    game_id: gameId || null,
                  });
                  if (res?.status === true) {
                    notification.success({
                      message: "Deleted today's big prize awards",
                      description: `${res.result?.deleted || 0} award(s) cleared for ${
                        res.result?.date_key || "today"
                      }.`,
                    });
                  } else {
                    notification.error({
                      message: "Error",
                      description: res?.message || "Unable to clear today's awards.",
                    });
                  }
                }}
              >
                Delete today&apos;s big prize
              </Button>
              <Button
                onClick={() => {
                  for (let i = fields.length - 1; i >= 0; i -= 1) {
                    remove(fields[i].name);
                  }
                }}
              >
                Clear All
              </Button>
              <Button
                type="primary"
                style={{ background: "#28a745", borderColor: "#28a745" }}
                icon={<PlusOutlined />}
                onClick={() =>
                    add({
                    config_id: newConfigId(),
                    distribution_type: "QuickPrize",
                    anytime_order_min: 1,
                    anytime_order_max: 5,
                    prize_kind: "Amount",
                    product_name: "",
                    big_prize_amount: undefined,
                    start_time: "00:00",
                    end_time: "23:59",
                    valid_date: undefined,
                    pos_numbers: [],
                    status: "A",
                  })
                }
              >
                Add Configuration
              </Button>
            </Space>
          </div>

          <p style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
            Listed <strong>POS numbers</strong> (exactly 4 digits) get a forced big prize in
            the listed window (Bahrain calendar day). Only <strong>pending</strong> windows
            are listed here. If a POS has already received a big prize, that configuration
            is hidden from this form but stays in the <strong>POS Monitor</strong> history.
            You can add another configuration for the same POS; the previous Winner Given
            row is kept. Each configuration is valid only for the <strong>Bahrain calendar
            day</strong> it was added and expires automatically after that date (or when
            the configured end time has passed today if still unused). Prize can be a{" "}
            <strong>cash amount</strong>, a <strong>product</strong> (Gold Coin, iPhone, etc.),
            or both. <strong>Quick Prize</strong> awards the <strong>first order</strong> in the
            Start–End window (Asia/Bahrain). <strong>Any time</strong> picks a{" "}
            <strong>random order number</strong> between min and max in that same window (e.g.
            orders 1–5); only that order wins. These wins do{" "}
            <strong>not</strong> debit pool Current Balance (Office Claim).
          </p>

          {fields.map(({ key, name, ...restField }) => (
            <div
              key={key}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
                background: "#fafbfc",
              }}
            >
              <Form.Item {...restField} name={[name, "config_id"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item {...restField} name={[name, "status"]} hidden initialValue="A">
                <Input />
              </Form.Item>
              <Form.Item {...restField} name={[name, "valid_date"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const validDate =
                    form.getFieldValue([field.name, name, "valid_date"]) || "";
                  const start =
                    form.getFieldValue([field.name, name, "start_time"]) || "";
                  const end =
                    form.getFieldValue([field.name, name, "end_time"]) || "";
                  const type =
                    form.getFieldValue([field.name, name, "distribution_type"]) ||
                    "QuickPrize";
                  return (
                    <div style={{ color: "#6f42c1", fontSize: 13, marginBottom: 8 }}>
                      Pending · valid {validDate || "today (Bahrain)"}
                      {start && end
                        ? ` · ${start}–${end} (Bahrain)${
                            type === "AnyTime" ? " · random order in range" : " · first order wins"
                          }`
                        : ""}
                    </div>
                  );
                }}
              </Form.Item>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="User Prize Distribution (POS Number)"
                    required
                    extra="Enter 4-digit POS numbers only (e.g. 1014). After Winner Given, add a new configuration for the same POS — history stays in POS Monitor."
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "pos_numbers"]}
                      noStyle
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!Array.isArray(value) || value.length < 1) {
                              return Promise.reject(
                                new Error("Add at least one 4-digit POS number.")
                              );
                            }
                            const invalid = value.filter((v) => !/^\d{4}$/.test(String(v)));
                            if (invalid.length) {
                              return Promise.reject(
                                new Error("POS numbers must be exactly 4 digits.")
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      getValueFromEvent={(values) =>
                        (values || [])
                          .map(normalizePosTag)
                          .filter(Boolean)
                      }
                    >
                      <Select
                        mode="tags"
                        tokenSeparators={[",", " ", ";", "\n"]}
                        placeholder="Enter 4-digit POS numbers"
                        style={{ width: "100%" }}
                        onInputKeyDown={(e) => {
                          if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                    <Space style={{ marginTop: 8 }} wrap>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={downloadSampleCsv}
                      >
                        Sample CSV
                      </Button>
                      <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={() => fileRefs.current[key]?.click()}
                      >
                        Upload CSV
                      </Button>
                      <input
                        ref={(el) => {
                          fileRefs.current[key] = el;
                        }}
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          importCsvIntoConfig(name, file);
                          e.target.value = "";
                        }}
                      />
                    </Space>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, "distribution_type"]}
                    label="Prize Distribution Type"
                    rules={[{ required: true, message: "Type is required." }]}
                    extra="Quick Prize: first order in window wins. Any time: random order # in range wins."
                  >
                    <Select>
                      <Option value="AnyTime">Any time</Option>
                      <Option value="QuickPrize">Quick Prize</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const type =
                      form.getFieldValue([field.name, name, "distribution_type"]) ||
                      "QuickPrize";
                    if (type !== "AnyTime") return null;
                    return (
                      <Col span={8}>
                        <Form.Item
                          label="Any Time order range"
                          required
                          extra="Random winning order between min and max (e.g. 1–5) within the time window."
                        >
                          <Space.Compact style={{ width: "100%" }}>
                            <Form.Item
                              {...restField}
                              name={[name, "anytime_order_min"]}
                              noStyle
                              initialValue={1}
                              {...intFieldProps()}
                              rules={[
                                { required: true, message: "Min required." },
                                {
                                  type: "number",
                                  min: 1,
                                  max: 1000,
                                  message: "Min must be 1–1000.",
                                },
                              ]}
                            >
                              <Input type="number" min={1} placeholder="Min" />
                            </Form.Item>
                            <span style={{ padding: "0 8px", lineHeight: "32px" }}>–</span>
                            <Form.Item
                              {...restField}
                              name={[name, "anytime_order_max"]}
                              noStyle
                              initialValue={5}
                              {...intFieldProps()}
                              rules={[
                                { required: true, message: "Max required." },
                                {
                                  type: "number",
                                  min: 1,
                                  max: 1000,
                                  message: "Max must be 1–1000.",
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min =
                                      Number(
                                        getFieldValue([
                                          field.name,
                                          name,
                                          "anytime_order_min",
                                        ])
                                      ) || 1;
                                    const max = Number(value) || 0;
                                    if (max < min) {
                                      return Promise.reject(
                                        new Error("Max must be ≥ min.")
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                }),
                              ]}
                            >
                              <Input type="number" min={1} placeholder="Max" />
                            </Form.Item>
                          </Space.Compact>
                        </Form.Item>
                      </Col>
                    );
                  }}
                </Form.Item>

                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, "prize_kind"]}
                    label="Big Prize Type"
                    initialValue="Amount"
                    rules={[{ required: true, message: "Type is required." }]}
                  >
                    <Select>
                      <Option value="Amount">Amount (cash)</Option>
                      <Option value="Product">Product</Option>
                      <Option value="Both">Amount + Product</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const kind =
                        form.getFieldValue([field.name, name, "prize_kind"]) ||
                        "Amount";
                      if (kind === "Product") return null;
                      return (
                        <Form.Item
                          {...restField}
                          name={[name, "big_prize_amount"]}
                          label="Big Prize Amount"
                          rules={[
                            {
                              required: true,
                              message: "Big prize amount is required.",
                            },
                          ]}
                          extra="Pick a prize slab amount or type any custom amount. Not counted in RTP."
                          getValueFromEvent={(value) => {
                            if (value == null || value === "") return undefined;
                            const n = Number(value);
                            return Number.isFinite(n) ? n : undefined;
                          }}
                          getValueProps={(value) => ({
                            value:
                              value == null || value === ""
                                ? undefined
                                : String(value),
                          })}
                          normalize={(value) => {
                            if (value == null || value === "") return undefined;
                            const n = Number(value);
                            return Number.isFinite(n) ? n : undefined;
                          }}
                        >
                          <AutoComplete
                            options={autocompleteOptions}
                            placeholder="Select slab or type amount"
                            style={{ width: "100%" }}
                            filterOption={(input, option) => {
                              const q = String(input || "").toLowerCase();
                              return (
                                String(option?.label || "")
                                  .toLowerCase()
                                  .includes(q) ||
                                String(option?.value || "").includes(q)
                              );
                            }}
                            allowClear
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const kind =
                        form.getFieldValue([field.name, name, "prize_kind"]) ||
                        "Amount";
                      if (kind === "Amount") return null;
                      return (
                        <Form.Item
                          {...restField}
                          name={[name, "product_name"]}
                          label="Product"
                          rules={[
                            {
                              required: true,
                              message: "Product name is required.",
                            },
                          ]}
                          extra="e.g. Gold Coin, iPhone. Winner claims at office."
                        >
                          <AutoComplete
                            options={[
                              { value: "Gold Coin" },
                              { value: "iPhone" },
                              { value: "iPad" },
                              { value: "Smart Watch" },
                              { value: "AirPods" },
                              { value: "Tablet" },
                            ]}
                            placeholder="Gold Coin, iPhone, …"
                            style={{ width: "100%" }}
                            filterOption={(input, option) =>
                              String(option?.value || "")
                                .toLowerCase()
                                .includes(String(input || "").toLowerCase())
                            }
                            allowClear
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    {...restField}
                    name={[name, "start_time"]}
                    label="Start Time (Bahrain)"
                    extra="Asia/Bahrain · Start of award window"
                  >
                    <Input type="time" />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    {...restField}
                    name={[name, "end_time"]}
                    label="End Time (Bahrain)"
                    extra="Asia/Bahrain · End of award window"
                  >
                    <Input type="time" />
                  </Form.Item>
                </Col>
              </Row>

              <Button danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                Remove configuration
              </Button>
            </div>
          ))}

          {!fields.length ? (
            <p style={{ color: "#888" }}>
              No configurations. Click “Add Configuration” to schedule a big prize.
            </p>
          ) : null}
        </div>
      )}
    </Form.List>
  );
}
